import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";

import "@progressive-development/pd-content/pd-tab";
import "@progressive-development/pd-forms/pd-input";
import "@progressive-development/pd-forms/pd-panel-button";
import { pdIcons } from "@progressive-development/pd-icon";
import type { TabHeader } from "@progressive-development/pd-content";

import { navigateOnClick, navigateTo } from "../../../../../common/router/app-navigation.js";
import { datumAusgeschrieben, tagesSchluessel } from "../../../../../common/format/de-format.js";
import { ROUTE_ENTSCHEIDUNGEN } from "../../../../../config/navigation.js";
import { STADT_NAME } from "../../../../../config/app-config.js";
import type { startseite } from "@progressive-development/politik-model";
import { parteiKurzname } from "../../../../wahlen/parteien.js";
import { blockStyles } from "../../../../../common/styles/block-styles.js";
import { auswahlZeile, stadtweitZurueck } from "../../../../../common/components/block-parts.js";
import { AUSWAHL_BESCHLUESSE, AUSWAHL_DRUCKSACHEN } from "./auswahl-texte.js";
import { instrumentText } from "../../../../entscheidungen/entscheidungen-labels.js";

const TAB_BESCHLUESSE = "beschluesse";
const TAB_DRUCKSACHEN = "drucksachen";

/**
 * Block 2 — Entscheidungen. Der Lebendigkeits-Beweis.
 *
 * Zwei Register statt zweier Listen nebeneinander: Für die Titel — bis 250
 * Zeichen im Gesamtbestand — ist kein Platz für zwei Spalten.
 *
 * Datumsgruppierung statt Datumsspalte: Die fünf jüngsten Beschlüsse stammen
 * alle vom selben Ratstermin; eine Spalte würde dasselbe Datum fünfmal
 * wiederholen. Die Zwischenüberschrift erzählt nebenbei etwas Wahres — dass in
 * einer Ratssitzung vieles auf einmal entschieden wird.
 *
 * Die Ergebnisklasse steht als kleines Mono-Label, NICHT als farbiger Chip:
 * Grün/Rot wäre eine Wertung.
 */
@customElement("app-entscheidungen-block")
@localized()
export class AppEntscheidungenBlock extends LitElement {
  /** Der Aufmacher des gemerkten Gebiets — Beschlüsse und neue Drucksachen. */
  @property({ attribute: false }) aufmacher?: startseite.AufmacherDokument;

  /** Parteien-Tabelle — löst den Slug der Urheber-Fraktion auf. */
  @property({ attribute: false }) parteien: startseite.ParteiEintrag[] = [];

  /**
   * Name des Bezirks, auf den sich die Listen beziehen; leer heißt stadtweit.
   *
   * Er steht in der ÜBERSCHRIFT — dort erkennt man den Ortsbezug sofort, so
   * wie beim Wahlen-Block („So hat Porz gewählt"). Gemeint ist immer der
   * BEZIRK: Ein gemerktes Sülz zeigt hier die Vorgänge Lindenthals, weil
   * Drucksachen auf Veedel-Ebene zu dünn sind.
   */
  @property() gebietName = "";

  /** Ob gerade ein Gebiet gemerkt ist — dann trägt der Block den Rückweg. */
  @property({ type: Boolean }) gefiltert = false;

  /**
   * Der gemerkte STADTTEIL, falls einer gemerkt ist — sonst leer.
   *
   * Die Listen zeigen den Bezirk (Drucksachen sind auf Veedel-Ebene zu dünn:
   * 401 von 3.364 nennen überhaupt einen Stadtteil). Wer Höhenhaus merkt und
   * hier Mülheim liest, soll aber nicht rätseln, sondern den Weg zur engeren
   * Sicht angeboten bekommen. Bewusst am Blockfuß und in der Hinweis-Form:
   * eine Auskunft, kein zweiter Aufmacher.
   */
  @property() stadtteilName = "";

  /** Slug dazu — führt auf die Liste, gefiltert auf diesen Stadtteil. */
  @property() stadtteilSlug = "";

  /** Sein Bezirk — die Liste filtert Stadtteile innerhalb eines Bezirks. */
  @property() stadtteilBezirkSlug = "";

  @state() private _aktivesRegister = TAB_BESCHLUESSE;

  @state() private _suchbegriff = "";

  static override styles = [
    blockStyles,
    css`
      /*
       * Register in Mono — sie gehören zur Bedienung, nicht zum Text.
       *
       * ÜBER DIE VARIABLE, nicht über ::part: pd-tab stellt keine Parts bereit
       * (geprüft am Quelltext), eine ::part-Regel liefe ins Leere. Die
       * Komponente setzt die Kopfschrift aus --pd-default-font-title-family.
       */
      pd-tab {
        display: block;
        margin-top: var(--portal-space-8);
        --pd-default-font-title-family: var(--portal-font-mono);
        --pd-tab-font-size: var(--portal-size-sm);
        --pd-tab-active-indicator-col: var(--portal-akzent);
        --pd-tab-text-col: var(--portal-ink-2);
        --pd-tab-active-text-col: var(--portal-ink);
        --pd-tab-hover-bg-col: transparent;
        /*
         * Polster NUR rechts: Ohne es kleben die Register aneinander, mit
         * symmetrischem Polster rückte das erste vom linken Rand ab — und der
         * ist die Kante, an der die ganze Seite ausgerichtet ist.
         *
         * Der Aktiv-Strich ist ein border-bottom am ganzen Kasten (nicht am
         * Wort) und wird dadurch etwas breiter als die Beschriftung. Eine
         * gap-Variable oder erreichbare Parts hat pd-tab nicht.
         */
        --pd-tab-header-padding: 0.5em 1.75em 0.5em 0;
        --pd-tab-icon-gap: 0;
      }
      /*
       * Erklärender Satz: Fließtext, nicht Metazeile — er erklärt die Sache,
       * nicht die Auswahl.
       *
       * OHNE Lesebreiten-Begrenzung: Ein einzelner Satz über die volle Breite
       * bleibt eine Zeile. Auf 62 Zeichen beschnitten würde daraus ein
       * vierzeiliger Absatz, der die Seite in zwei Spalten zu teilen scheint —
       * die Begrenzung gilt für Fließtext, nicht für eine Zeile.
       */
      .erklaerung {
        margin: var(--portal-space-4) 0 0;
        font-size: var(--portal-size-sm);
        color: var(--portal-ink-2);
      }
      .register-inhalt {
        padding-top: var(--portal-space-2);
      }
      /* Auswahl-Zeile links, Suchknopf rechts — eine Zeile, kein Kasten. */
      .kopfzeile {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--portal-space-4);
        flex-wrap: wrap;
      }
      .kopfzeile .auswahl {
        flex: 1 1 20rem;
      }
      /*
       * Das Panel soll NUR das Eingabefeld sein — kein Kasten um ein Feld
       * herum. Dafür fällt alles weg, was das Panel selbst mitbringt:
       *
       * - hide-close: Zum Schließen genügen Escape und der Klick nach außen,
       *   beides bringt die Komponente mit. Ein X über einem einzelnen Feld
       *   ist ein Bedienelement für ein Bedienelement.
       * - Polster auf 0, Hintergrund durchsichtig, kein Radius, kein Schatten:
       *   Das Feld hat Rahmen und Innenschatten schon selbst.
       *
       * Panel und Knopf lesen VERSCHIEDENE Tokens (Panel: --pd-radius-lg,
       * --pd-shadow-md; Knopf: --pd-button-border-radius, --pd-shadow-inset-*)
       * — deshalb entstellt das Abschalten hier den Knopf nicht.
       *
       * Volle Breite braucht ZWEI Regeln, nicht eine — so macht es auch
       * djkollektiv (djk-location-filter):
       *
       * - --pd-input-field-width: 100% wirkt INNEN (Vorgabe: feste 250 px);
       *   die 100 % beziehen sich auf den Host.
       * - width: 100% am Host selbst, denn der ist ohne eigene Breite so
       *   schmal wie sein Inhalt — die innere Prozentangabe hätte sonst keinen
       *   Bezug und das Feld bliebe bei 250 px stehen.
       */
      .suche-knopf {
        flex: 0 0 auto;
        --pd-panel-min-width: 22rem;
        --pd-panel-max-width: 22rem;
        --pd-panel-padding: 0;
        --pd-panel-bg-col: transparent;
        --pd-radius-lg: 0;
        --pd-shadow-md: none;
        --pd-input-field-width: 100%;
        --pd-input-icon-color: var(--portal-ink-2);
      }
      .suche-knopf pd-input {
        display: block;
        width: 100%;
      }
      .aktenzeichen {
        display: block;
        margin-bottom: var(--portal-space-2);
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
        letter-spacing: 0.02em;
      }
    `,
    css`
      /* Der Ortshinweis am Blockfuß: Mono, zweite Tinte, eine Zeile Luft nach
         oben — sichtbar für den, der ihn sucht, still für alle anderen. */
      .ortshinweis {
        margin: var(--portal-space-4) 0 0;
        max-width: 64ch;
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }
      .ortshinweis a {
        color: inherit;
      }
    `,
  ];

  private get _tabs(): TabHeader[] {
    return [
      { key: TAB_BESCHLUESSE, name: msg("Beschlüsse", { id: "app.start.entsch.tab.beschluesse" }) },
      {
        key: TAB_DRUCKSACHEN,
        name: msg("Neu eingebracht", { id: "app.start.entsch.tab.drucksachen" }),
      },
    ];
  }

  /**
   * Ziel ist die Detailseite über den sprechenden Slug (E1, in der Pipeline
   * gebildet). Fehlt er, führt der Weg über die Referenz — URL-kodiert, nicht
   * umgeformt: Aus `AN/0673/2026` ein `an-0673-2026` zu bauen hieße, einen
   * Schlüssel zu erfinden.
   */
  private _ziel(e: startseite.DrucksacheEintrag): string {
    return `/${ROUTE_ENTSCHEIDUNGEN}/${e.slug ?? encodeURIComponent(e.referenz)}`;
  }

  /**
   * `urheberFraktion` ist ein kuratierter SLUG ("spd"), kein Anzeigename.
   * Aufgelöst wird ausschließlich über die Tabelle; ist der Slug dort
   * unbekannt, wird er sichtbar durchgereicht statt geraten oder verschluckt.
   */
  private _urheberName(slug: string): string {
    return parteiKurzname(this.parteien, slug);
  }

  /** Wer den Knopf drückt, will tippen — nicht erst noch ins Feld klicken. */
  private _panelUmgeschaltet(e: CustomEvent<{ open: boolean }>): void {
    if (!e.detail?.open) return;
    void this.updateComplete.then(() => {
      this.querySelector<HTMLElement>("pd-input")?.focus();
    });
  }

  /**
   * Ohne Sofortvorschläge: ein ehrliches Feld, das in die Liste führt.
   * Die `?q=`-Schnittstelle ist dieselbe wie vorher, sie wird nur von hier
   * gefüttert.
   */
  private _suchen(): void {
    const q = this._suchbegriff.trim();
    navigateTo(
      q ? `/${ROUTE_ENTSCHEIDUNGEN}?q=${encodeURIComponent(q)}` : `/${ROUTE_ENTSCHEIDUNGEN}`,
    );
  }

  /** Gruppiert nach Kalendertag, Reihenfolge der Daten bleibt erhalten. */
  private _gruppiere<T>(eintraege: T[], schluessel: (e: T) => string): [string, T[]][] {
    const gruppen = new Map<string, T[]>();
    for (const eintrag of eintraege) {
      const key = schluessel(eintrag);
      const vorhanden = gruppen.get(key);
      if (vorhanden) vorhanden.push(eintrag);
      else gruppen.set(key, [eintrag]);
    }
    return [...gruppen.entries()];
  }

  private _renderBeschluesse(): TemplateResult {
    const eintraege = this.aufmacher?.neuesteBeschluesse ?? [];
    if (eintraege.length === 0) return this._renderLeer();

    // Ein Termin ist Datum UND Gremium — beides steht in der Zwischenüberschrift.
    const gruppen = this._gruppiere(
      eintraege,
      (e) => `${tagesSchluessel(e.entschiedenAm ?? "")}|${e.gremium ?? ""}`,
    );

    return html`${gruppen.map(([, gruppe]) => {
      const erster = gruppe[0];
      return html`
        <h3 class="gruppe-titel">
          ${[
            erster.entschiedenAm ? datumAusgeschrieben(erster.entschiedenAm) : undefined,
            erster.gremium,
          ]
            .filter(Boolean)
            .join(" · ")}
        </h3>
        <ul class="liste">
          ${gruppe.map((e) => this._renderBeschluss(e))}
        </ul>
      `;
    })}`;
  }

  private _renderBeschluss(e: startseite.BeschlussEintrag): TemplateResult {
    const ziel = this._ziel(e);
    return html`
      <li>
        <a class="zeile" href=${ziel} @click=${(ev: Event) => navigateOnClick(ev, ziel)}>
          <span class="aktenzeichen">${e.referenz}</span>
          <p class="titel">${e.name}</p>
          <p class="zeilen-meta">
            <!-- Wortlaut der Quelle, keine eigene Einstufung. -->
            <span>${e.ergebnis}</span>
          </p>
        </a>
      </li>
    `;
  }

  private _renderDrucksachen(): TemplateResult {
    const eintraege = this.aufmacher?.neueDrucksachen ?? [];
    if (eintraege.length === 0) return this._renderLeer();

    const gruppen = this._gruppiere(eintraege, (e) => tagesSchluessel(e.datum));

    return html`${gruppen.map(([, gruppe]) => {
      const erster = gruppe[0];
      return html`
        <h3 class="gruppe-titel">${datumAusgeschrieben(erster.datum)}</h3>
        <ul class="liste">
          ${gruppe.map((e) => this._renderDrucksache(e))}
        </ul>
      `;
    })}`;
  }

  private _renderDrucksache(e: startseite.DrucksacheEintrag): TemplateResult {
    const ziel = this._ziel(e);
    return html`
      <li>
        <a class="zeile" href=${ziel} @click=${(ev: Event) => navigateOnClick(ev, ziel)}>
          <span class="aktenzeichen">${e.referenz}</span>
          <p class="titel">${e.name}</p>
          <p class="zeilen-meta">
            <!-- Unsere Einordnung, aus dem Slug aufgelöst — unbekannte Slugs
                 bleiben sichtbar stehen. -->
            <span>${instrumentText(e.instrument)}</span>
            <!-- Urheber nur, wenn die Quelle einen nennt. -->
            ${
              e.urheberFraktion
                ? html`<span>${this._urheberName(e.urheberFraktion)}</span>`
                : nothing
            }
          </p>
        </a>
      </li>
    `;
  }

  /**
   * Leerzustand: Auswahl-Zeile bleibt stehen, dazu ein Satz mit dem Grund.
   *
   * Der Block verschwindet nicht — sonst wirkte die gefilterte Startseite
   * kaputt statt leer, und niemand wüsste, ob es nichts gibt oder etwas fehlt.
   */
  private _renderLeer(): TemplateResult {
    return html`<p class="hinweis">
      ${
        this.gebietName
          ? msg(str`Für dieses Kriterium liegt in ${this.gebietName} gerade kein Eintrag vor.`, {
              id: "app.start.entsch.leer.gebiet",
            })
          : msg("Für dieses Kriterium liegt gerade kein Eintrag im Bestand.", {
              id: "app.start.entsch.leer",
            })
      }
    </p>`;
  }

  /**
   * Die Brücke zwischen gemerktem Veedel und angezeigtem Bezirk.
   *
   * Ohne gemerkten Stadtteil fällt sie ganz weg — kein Leerraum, keine Zeile
   * über etwas, das niemand gefragt hat. Keine Anzahl: Die stünde nur im
   * Listen-Artefakt, und dafür lädt die Startseite es nicht.
   */
  private _renderOrtshinweis() {
    if (!this.stadtteilSlug || !this.stadtteilName) return nothing;
    const ziel =
      `/${ROUTE_ENTSCHEIDUNGEN}` +
      `?bezirk=${encodeURIComponent(this.stadtteilBezirkSlug)}` +
      `&stadtteil=${encodeURIComponent(this.stadtteilSlug)}`;
    return html`<p class="ortshinweis">
      ${msg(str`Hier stehen Vorgänge des Bezirks ${this.gebietName}.`, {
        id: "app.start.entsch.ort.satz",
      })}
      <a href=${ziel} @click=${(e: Event) => navigateOnClick(e, ziel)}
        >${msg(str`Drucksachen, die ${this.stadtteilName} nennen`, {
          id: "app.start.entsch.ort.weg",
        })}
        →</a
      >
    </p>`;
  }

  override render() {
    const auswahl =
      this._aktivesRegister === TAB_BESCHLUESSE ? AUSWAHL_BESCHLUESSE() : AUSWAHL_DRUCKSACHEN();

    return html`
      <h2>
        ${msg(str`Das wird gerade in ${this.gebietName || STADT_NAME} entschieden`, {
          id: "app.start.entsch.titel",
        })}
      </h2>
      <!--
        Die Auswahl-Zeile folgt dem aktiven Register: jedes hat sein eigenes
        Kriterium. Rechts daneben die Suche als pd-panel-button: ein Knopf, aus
        dem ein Panel mit dem Feld fährt — statt eines Formulars, das dauerhaft
        Platz belegt. Schließen übernimmt die Komponente (Escape, Klick nach
        außen, eigener Schließer im Panel).

        In DIESER Zeile, weil sie die einzige ist, die zum Block gehört und
        nicht zu seinen Daten: Über den Registern schöbe sich ein Feld zwischen
        Aussage und Daten, unter den Listen stünde die Suche hinter dem, was
        sie durchsucht. Auf schmalen Schirmen rutscht der Knopf unter die Zeile.
      -->
      <div class="kopfzeile">
        ${auswahlZeile(auswahl)}
        <pd-panel-button
          outline
          right
          hide-close
          size="sm"
          class="suche-knopf"
          icon=${pdIcons.ICON_SEARCH}
          text=${msg("Drucksachen suchen", { id: "app.start.suche.label" })}
          @panel-button-toggled=${this._panelUmgeschaltet}
        >
          <!--
            OHNE Label: Den Zweck trägt der Knopf, aus dem das Panel fährt
            („Drucksachen suchen"). Das Label der Bibliothek ist kein Text,
            sondern ein Flex-Kasten mit eigener Breite und min-width 150px
            (Platz für Hilfe-Icon und Pflichtzeichen) — im schmalen Panel
            stellte er sich neben das Feld statt darüber.
          -->
          <pd-input
            no-label
            icon-right
            icon=${pdIcons.ICON_SEARCH}
            clickableIcon
            iconLabel=${msg("Drucksachen suchen", { id: "app.start.suche.label" })}
            placeHolder=${msg("Titel oder Aktenzeichen", { id: "app.start.suche.platzhalter" })}
            initValue=${this._suchbegriff}
            @pd-form-element-change=${(e: CustomEvent) =>
              (this._suchbegriff = e.detail.value ?? "")}
            @input-icon-click=${this._suchen}
            @enter-pressed=${this._suchen}
          ></pd-input>
        </pd-panel-button>
      </div>

      <!--
        Ein Satz zur Sache, an derselben Stelle wie die Verweiszeile im
        Wahlen-Block. Er erklärt den BEGRIFF, nicht die Auswahl — die steht
        eine Zeile höher. Nach dem Arbeitsblatt (Block 2): ein
        Beschlussgegenstand mit Aktenzeichen und Beratungsweg, wobei der Weg
        durch die Gremien das Besondere ist. EIN Satz, keine zwei — der Block
        soll erklären, nicht einführen.
      -->
      <p class="erklaerung">
        ${msg(
          "Eine Drucksache ist ein Vorgang, über den entschieden werden soll; sie wandert durch die Gremien und trägt an jeder Station ein Ergebnis.",
          { id: "app.start.entsch.erklaerung" },
        )}
      </p>

      <pd-tab
        .tabs=${this._tabs}
        defaultTab=${TAB_BESCHLUESSE}
        @tab-selected=${(e: CustomEvent) => (this._aktivesRegister = e.detail?.key ?? e.detail)}
      >
        <div slot=${TAB_BESCHLUESSE} class="register-inhalt">${this._renderBeschluesse()}</div>
        <div slot=${TAB_DRUCKSACHEN} class="register-inhalt">${this._renderDrucksachen()}</div>
      </pd-tab>

      ${this._renderOrtshinweis()}

      <a
        class="weiter"
        href="/${ROUTE_ENTSCHEIDUNGEN}"
        @click=${(e: Event) => navigateOnClick(e, `/${ROUTE_ENTSCHEIDUNGEN}`)}
        >${msg("Alle Drucksachen", { id: "app.start.entsch.alle" })} →</a
      >${stadtweitZurueck(this.gefiltert)}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-entscheidungen-block": AppEntscheidungenBlock;
  }
}
